import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  TextInput,
  SimpleGrid,
  UnstyledButton,
  Stack,
  Group,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { productsService } from '../../services/products';
import { CATEGORY_EMOJI } from '../../utils/categories';

const cardStyle = {
  borderRadius: '0.75rem',
  padding: '1rem',
  border: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
  backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
  transition: 'background-color 150ms ease, border-color 150ms ease',
  display: 'flex' as const,
  flexDirection: 'column' as const,
  gap: '0.5rem',
};

export default function Categories() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const { data: categoriesGrouped = {} } = useQuery({
    queryKey: ['categories-grouped'],
    queryFn: () => productsService.getCategoriesGrouped(),
    staleTime: 300_000,
  });

  const sections = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return Object.entries(categoriesGrouped)
      .map(([parent, children]) => ({
        parent,
        children: q ? children.filter((c) => c.toLowerCase().includes(q)) : [...children].sort((a, b) => a.localeCompare(b)),
      }))
      .filter((s) => s.children.length > 0);
  }, [categoriesGrouped, filter]);

  return (
    <Container size="lg" pt="md" pb="xl">
      <Stack gap="xl">
        <Stack gap={4}>
          <Title order={2}>Browse Categories</Title>
          <Text c="dimmed" size="sm">Find reviews by what you're shopping for</Text>
        </Stack>

        <TextInput
          placeholder="Filter categories…"
          leftSection={<IconSearch size={16} />}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          style={{ maxWidth: 320 }}
        />

        {sections.length === 0 ? (
          <Text c="dimmed">No categories match "{filter}"</Text>
        ) : (
          <Stack gap="xl">
            {sections.map(({ parent, children }) => (
              <Stack key={parent} gap="sm">
                <Group gap="xs" align="center">
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{CATEGORY_EMOJI[parent] ?? '🗂️'}</span>
                  <Text fw={700} size="md">{parent}</Text>
                </Group>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
                  {children.map((category) => (
                    <UnstyledButton
                      key={category}
                      onClick={() => navigate(`/categories/${encodeURIComponent(category)}`)}
                      style={cardStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))';
                        e.currentTarget.style.borderColor = 'var(--mantine-color-brand-4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = cardStyle.backgroundColor;
                        e.currentTarget.style.borderColor = 'light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))';
                      }}
                    >
                      <Stack align="center" gap={8} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 32, lineHeight: 1 }}>
                          {CATEGORY_EMOJI[category] ?? '📦'}
                        </span>
                        <Text size="sm" fw={500} lh={1.3}>{category}</Text>
                      </Stack>
                    </UnstyledButton>
                  ))}
                </SimpleGrid>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
